import type { OnInit } from '@angular/core';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { WebWidgetEntity } from '@web/types';

import { ChangeDetectionStrategy, Component, DestroyRef, inject, input } from '@angular/core';

import { ERadioGroupType } from '@ui/sp-radio-group';
import { ELineStyle } from '@common/enums/line-style.enum';

import { LINE_SIZE_BOUNDS } from '@sites/entity-core';
import type { LineForm, LinePropsModel } from '@sites/entity-signal-form';

import { WebWidgetSignalFormService } from '@web/core/services/web-widget-signal-form.service';

import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { WebColorPickerV2Component } from '@web/shared/components/web-color-picker-v2/web-color-picker-v2.component';

/**
 * Standalone, `input()`-based control panel for the `line` (divider) widget.
 * Every setting writes the DESKTOP leaf of its `props` entry through the shared
 * signal form.
 */
@Component({
    selector: 'web-line-control',
    templateUrl: './line-control.component.html',
    styleUrls: ['./line-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, WebColorPickerV2Component],
})
export class LineControlComponent implements OnInit {
    public readonly widget = input.required<WebWidgetEntity>();
    public readonly isSpecific = input<boolean>(false);
    public readonly isEduSite = input<boolean>(false);

    private readonly signalFormService: WebWidgetSignalFormService = inject(WebWidgetSignalFormService);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    public readonly ERadioGroupType = ERadioGroupType;
    public readonly ELineStyle = ELineStyle;

    /** Editing bounds declared once on the schema — both inputs are 0–100. */
    public readonly SIZE_MIN = LINE_SIZE_BOUNDS.min;
    public readonly SIZE_MAX = LINE_SIZE_BOUNDS.max;

    public form!: LineForm;

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<LineForm>(this.widget().id, this.destroyRef);
    }

    /** The picker hands back a theme preset name when one was chosen; it wins over the raw value. */
    public setBorderTopColor({ value, presetName }: ColorPickerEventPayload): void {
        this.form.setBorderTopColor(presetName ?? value);
    }

    /** The radio group emits a plain string; narrowed to the schema's alignment bucket. */
    public setHorizontalAlign(value: string): void {
        this.form.setHorizontalAlign(value as LinePropsModel['horizontalAlign']['desktop']);
    }
}
